import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import { themeWorkspaceService } from '@/services/theme-workspace.service'
import { themeService } from '@/services/theme.service'
import { deepEqual } from '@/lib/deep-equal'
import type {
  Overlay,
  PageSection,
  Proposal,
  SectionType,
  StoreThemeDraft,
  ThemeSchema,
  WorkspaceHistoryEntry,
  WorkspaceHistoryState,
  WorkspaceState,
} from '@/types/theme-workspace'

export type SaveStatus = 'idle' | 'saving' | 'error'
export type Viewport = 'desktop' | 'tablet' | 'mobile'

export interface UndoEntry {
  id: string
  label: string
  timestamp: number
  revertOverlay: Overlay
  revertSectionsByPage: Record<string, PageSection[]>
}

export interface StorefrontMetadata {
  subdomain?: string | null
}

interface ProposalSectionPatch {
  theme_section_id?: string
  page?: string
  content?: Record<string, unknown>
  settings?: Record<string, unknown>
  visible?: boolean
  variant_id?: string
}

const UNDO_STACK_STORAGE_KEY = 'pageBuilderUndoStackByStore'

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function readStoredUndoStackByStore() {
  try {
    const raw = globalThis.localStorage?.getItem(UNDO_STACK_STORAGE_KEY)
    if (!raw) return {}

    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {}
    }

    return parsed as Record<string, UndoEntry[]>
  } catch {
    return {}
  }
}

function writeStoredUndoStackByStore(value: Record<string, UndoEntry[]>) {
  try {
    globalThis.localStorage?.setItem(UNDO_STACK_STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Local persistence should never block the builder itself.
  }
}

function cloneSectionsByPage(nextSectionsByPage: Record<string, PageSection[]> | undefined) {
  return Object.fromEntries(
    Object.entries(nextSectionsByPage ?? {}).map(([pageKey, sections]) => [
      pageKey,
      sections.map((section) => cloneJson(section)),
    ]),
  ) as Record<string, PageSection[]>
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function recordValue(value: unknown): Record<string, unknown> {
  return isRecord(value) ? cloneJson(value) : {}
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

function numberValue(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function reorderPageSections(sections: PageSection[], orderedIds: string[]) {
  const uniqueOrderedIds = orderedIds.filter(
    (id, index) => typeof id === 'string' && id.length > 0 && orderedIds.indexOf(id) === index,
  )
  const orderedIdSet = new Set(uniqueOrderedIds)
  const sectionById = new Map(sections.map((section) => [section.theme_section_id, section]))

  const orderedSections = uniqueOrderedIds
    .map((id) => sectionById.get(id))
    .filter((section): section is PageSection => Boolean(section))

  const remainingSections = sections.filter((section) => !orderedIdSet.has(section.theme_section_id))

  return [...orderedSections, ...remainingSections].map((section, index) => ({
    ...section,
    sort_order: index,
  }))
}

function createSectionInstance(
  sectionType: SectionType,
  themeSectionId: string,
  pageName: string,
  sortOrder: number,
): PageSection {
  const activeVariant = sectionType.variants.find(
    (variant) => variant.variant_id === sectionType.default_variant_id,
  ) ?? sectionType.variants[0]

  if (!activeVariant) {
    throw new Error(`Section type ${sectionType.section_type_code} has no variants.`)
  }

  return {
    theme_section_id: themeSectionId,
    section_id: themeSectionId,
    section_type_code: sectionType.section_type_code,
    section_code: sectionType.section_type_code,
    section_type_name: sectionType.name,
    page: pageName,
    sort_order: sortOrder,
    visible: true,
    variant_id: activeVariant.variant_id,
    template_key: activeVariant.template_key,
    schema: cloneJson(activeVariant.schema),
    default_content: cloneJson(activeVariant.default_content ?? {}),
    default_settings: cloneJson(activeVariant.default_settings ?? {}),
    variants: cloneJson(sectionType.variants),
  }
}

function addedOverlaySectionsByPage(nextSchema: ThemeSchema, overlay?: Overlay | null) {
  const added = Array.isArray(overlay?._sections_added) ? overlay._sections_added : []
  const sectionsByPage: Record<string, PageSection[]> = {}

  for (const [index, rawSection] of added.entries()) {
    if (!isRecord(rawSection)) continue

    const sectionTypeCode = stringValue(rawSection.section_type_code)
    if (!sectionTypeCode) continue

    const sectionType = nextSchema.section_types.find(
      candidate => candidate.section_type_code === sectionTypeCode,
    )
    if (!sectionType || sectionType.variants.length === 0) continue

    const variantId = stringValue(rawSection.variant_id) ?? sectionType.default_variant_id
    const activeVariant = sectionType.variants.find(
      variant => variant.variant_id === variantId,
    ) ?? sectionType.variants[0]
    if (!activeVariant) continue

    const pageName = stringValue(rawSection.page) ?? 'home'
    const sectionId = stringValue(rawSection.id)
      ?? stringValue(rawSection.section_id)
      ?? `draft:${pageName}:${sectionTypeCode}:${index + 1}`
    const sectionCode = stringValue(rawSection.section_code)
      ?? `draft.${pageName}.${sectionTypeCode}.${sectionId}`

    sectionsByPage[pageName] ??= []
    sectionsByPage[pageName].push({
      theme_section_id: sectionId,
      section_id: sectionId,
      section_type_code: sectionTypeCode,
      section_code: sectionCode,
      section_type_name: sectionType.name,
      page: pageName,
      sort_order: numberValue(rawSection.sort_order, sectionsByPage[pageName].length),
      visible: typeof rawSection.visible === 'boolean' ? rawSection.visible : true,
      variant_id: activeVariant.variant_id,
      template_key: activeVariant.template_key,
      schema: cloneJson(activeVariant.schema),
      default_content: recordValue(rawSection.default_content ?? activeVariant.default_content),
      default_settings: recordValue(rawSection.default_settings ?? activeVariant.default_settings),
      variants: cloneJson(sectionType.variants),
    })
  }

  return sectionsByPage
}

function normalizeSectionOrderPatch(value: unknown, fallbackPage: string) {
  if (Array.isArray(value)) {
    return {
      [fallbackPage]: value.filter((id): id is string => typeof id === 'string'),
    }
  }

  if (!value || typeof value !== 'object') {
    return {}
  }

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).flatMap(([pageKey, sectionIds]) => {
      if (!Array.isArray(sectionIds)) {
        return []
      }

      return [[pageKey, sectionIds.filter((id): id is string => typeof id === 'string')] as const]
    }),
  )
}

function buildSparseRestorePatch(
  currentValues: Record<string, unknown> | null | undefined,
  targetValues: Record<string, unknown> | null | undefined,
) {
  const patch: Record<string, unknown> = {}
  const keys = new Set([
    ...Object.keys(currentValues ?? {}),
    ...Object.keys(targetValues ?? {}),
  ])

  for (const key of keys) {
    const currentValue = currentValues?.[key]
    const hasTargetKey = Boolean(targetValues) && Object.prototype.hasOwnProperty.call(targetValues, key)
    const targetValue = hasTargetKey ? targetValues?.[key] : null

    if (!deepEqual(currentValue, targetValue)) {
      patch[key] = targetValue
    }
  }

  return patch
}

export const useThemeWorkspaceStore = defineStore('themeWorkspace', () => {
  /* ------------------------------------------------------------------ */
  /*  Core state                                                         */
  /* ------------------------------------------------------------------ */
  const storeId = ref<string | null>(null)
  const page = ref<string>('home')
  const draft = ref<StoreThemeDraft | null>(null)
  const appliedOverlay = ref<Overlay | null>(null)
  const schema = ref<ThemeSchema | null>(null)
  const sectionsByPage = ref<Record<string, PageSection[]>>({})
  const storeSubdomain = ref<string | null>(null)
  const iframePreviewToken = ref<string | null>(null)
  const canvasIframe = ref<HTMLIFrameElement | null>(null)

  /* ------------------------------------------------------------------ */
  /*  UI state                                                           */
  /* ------------------------------------------------------------------ */
  const saveStatus = ref<SaveStatus>('idle')
  const lastError = ref<string | null>(null)
  const isLoading = ref(false)
  const isPublishing = ref(false)
  const viewport = useLocalStorage<Viewport>('pageBuilderViewport', 'desktop')
  const undoStackByStore = ref<Record<string, UndoEntry[]>>(readStoredUndoStackByStore())
  const historyEntries = ref<WorkspaceHistoryEntry[]>([])
  const historyCurrentSequence = ref<number | null>(null)
  const canUndo = ref(false)
  const canRedo = ref(false)

  /* ------------------------------------------------------------------ */
  /*  Undo stack                                                         */
  /* ------------------------------------------------------------------ */
  const undoStack = ref<UndoEntry[]>([])
  let suppressUndoCapture = 0

  /* ------------------------------------------------------------------ */
  /*  Derived                                                            */
  /* ------------------------------------------------------------------ */
  const hasUnsavedChanges = computed(() => {
    if (!draft.value) return false

    return !deepEqual(draft.value.draft_overlay ?? {}, appliedOverlay.value ?? {})
  })

  const isHybridPage = computed(() => ['cart', 'checkout'].includes(page.value))

  /**
   * Concrete section instances for the current page, from the schema's sections_by_page.
   * Each item has both theme_section_id (UUID for mutations) and section_type_code (for overlay keys).
   */
  const currentPageSections = computed<PageSection[]>(() => {
    return sectionsByPage.value[page.value] ?? []
  })
  function syncSectionsFromSchema(nextSchema: ThemeSchema | null, overlay?: Overlay | null) {
    const nextSectionsByPage = cloneSectionsByPage(nextSchema?.sections_by_page)
    if (nextSchema) {
      const addedSectionsByPage = addedOverlaySectionsByPage(nextSchema, overlay)
      for (const [pageKey, addedSections] of Object.entries(addedSectionsByPage)) {
        const existingSections = nextSectionsByPage[pageKey] ?? []
        const existingIds = new Set(existingSections.map(section => section.theme_section_id))
        nextSectionsByPage[pageKey] = [
          ...existingSections,
          ...addedSections.filter(section => !existingIds.has(section.theme_section_id)),
        ].sort((a, b) => a.sort_order - b.sort_order)
      }
    }
    const sectionOrderByPage = normalizeSectionOrderPatch(overlay?._section_order, page.value)

    for (const [pageKey, sectionIds] of Object.entries(sectionOrderByPage)) {
      nextSectionsByPage[pageKey] = reorderPageSections(nextSectionsByPage[pageKey] ?? [], sectionIds)
    }

    const overlayEntries = Object.entries(overlay ?? {}).filter(([key]) => !key.startsWith('_'))
    for (const [sectionKey, value] of overlayEntries) {
      if (!value || typeof value !== 'object') {
        continue
      }

      const patch = value as ProposalSectionPatch
      const matches = Object.values(nextSectionsByPage).flatMap((sections) =>
        sections.filter((section) => section.section_type_code === sectionKey || section.section_code === sectionKey),
      )

      if (matches.length !== 1) {
        continue
      }

      const targetSectionId = matches[0]?.theme_section_id
      if (!targetSectionId) {
        continue
      }

      for (const [pageKey, sections] of Object.entries(nextSectionsByPage)) {
        nextSectionsByPage[pageKey] = sections.map((section) => {
          if (section.theme_section_id !== targetSectionId) {
            return section
          }

          return {
            ...section,
            visible: typeof patch.visible === 'boolean' ? patch.visible : section.visible,
            variant_id: typeof patch.variant_id === 'string' ? patch.variant_id : section.variant_id,
          }
        })
      }
    }

    sectionsByPage.value = nextSectionsByPage
  }

  function applyWorkspaceState(workspace: WorkspaceState) {
    draft.value = workspace.draft
    appliedOverlay.value = workspace.applied_overlay

    if (schema.value) {
      syncSectionsFromSchema(schema.value, draft.value?.draft_overlay)
    }

    broadcastDraftOverlayToIframe()
  }

  function updateSectionInstance(
    sectionId: string,
    updater: (section: PageSection) => PageSection,
  ) {
    sectionsByPage.value = Object.fromEntries(
      Object.entries(sectionsByPage.value).map(([pageKey, sections]) => [
        pageKey,
        sections.map((section) =>
          section.theme_section_id === sectionId ? updater(section) : section,
        ),
      ]),
    )
  }

  function removeSectionInstance(sectionId: string) {
    sectionsByPage.value = Object.fromEntries(
      Object.entries(sectionsByPage.value).map(([pageKey, sections]) => [
        pageKey,
        sections.filter((section) => section.theme_section_id !== sectionId),
      ]),
    )
  }

  function findSectionById(sectionId: string) {
    for (const [pageKey, sections] of Object.entries(sectionsByPage.value)) {
      const section = sections.find((candidate) => candidate.theme_section_id === sectionId)
      if (section) {
        return { pageKey, section }
      }
    }

    return null
  }

  function resolveProposalSectionTarget(sectionKey: string, value: ProposalSectionPatch = {}): PageSection {
    if (typeof value.theme_section_id === 'string') {
      const exactMatch = findSectionById(value.theme_section_id)
      if (exactMatch) {
        return exactMatch.section
      }

      throw new Error(`Section ${value.theme_section_id} is not available in the current workspace.`)
    }

    const pageHint = typeof value.page === 'string' ? value.page : page.value
    const pageMatches = (sectionsByPage.value[pageHint] ?? []).filter(
      (section) => section.section_type_code === sectionKey || section.section_code === sectionKey,
    )

    if (pageMatches.length === 1) {
      return pageMatches[0]!
    }

    const allMatches = Object.values(sectionsByPage.value).flatMap((sections) =>
      sections.filter((section) => section.section_type_code === sectionKey || section.section_code === sectionKey),
    )

    if (allMatches.length === 1) {
      return allMatches[0]!
    }

    if (allMatches.length > 1) {
      throw new Error(`Section ${sectionKey} is ambiguous without a theme_section_id.`)
    }

    throw new Error(`Section ${sectionKey} is not available in the current workspace.`)
  }

  async function confirmProposal(proposal: Proposal) {
    if (!storeId.value) throw new Error('No store selected')
    if (!proposal.confirmation_token) throw new Error('Proposal is missing a confirmation token.')

    const changedFieldKeys = new Set<string>()
    const changedSectionIds = new Set<string>()
    const patch = proposal.preview_overlay ?? {}
    const priorOverlay = draft.value?.draft_overlay ? cloneJson(draft.value.draft_overlay) : {}
    const priorSectionsByPage = cloneSectionsByPage(sectionsByPage.value)

    if (patch._style && typeof patch._style === 'object') {
      Object.keys(patch._style).forEach((fieldKey) => changedFieldKeys.add(fieldKey))
    }

    if (patch._branding && typeof patch._branding === 'object') {
      Object.keys(patch._branding).forEach((fieldKey) => changedFieldKeys.add(fieldKey))
    }

    for (const [sectionKey, sectionPatch] of Object.entries(patch)) {
      if (sectionKey.startsWith('_') || !sectionPatch || typeof sectionPatch !== 'object') {
        continue
      }

      const targetSection = resolveProposalSectionTarget(sectionKey)
      changedSectionIds.add(targetSection.theme_section_id)

      const sectionPatchRecord = sectionPatch as ProposalSectionPatch
      Object.keys(sectionPatchRecord.content ?? {}).forEach((fieldKey) => changedFieldKeys.add(fieldKey))
      Object.keys(sectionPatchRecord.settings ?? {}).forEach((fieldKey) => changedFieldKeys.add(fieldKey))
    }

    saveStatus.value = 'saving'
    lastError.value = null

    try {
      const { data } = await themeWorkspaceService.confirmProposal(
        storeId.value,
        proposal.proposal_id,
        { confirmation_token: proposal.confirmation_token },
      )

      if (data.data.workspace) {
        applyWorkspaceState(data.data.workspace)
      }
      await loadHistory()

      if (draft.value?.draft_overlay && suppressUndoCapture === 0) {
        pushUndo({
          id: crypto.randomUUID?.() || `${Date.now()}`,
          label: proposal.human_summary,
          timestamp: Date.now(),
          revertOverlay: priorOverlay,
          revertSectionsByPage: priorSectionsByPage,
        })
      }

      saveStatus.value = 'idle'

      return {
        changedFieldKeys: [...changedFieldKeys],
        changedSectionIds: [...changedSectionIds],
      }
    } catch (err: unknown) {
      saveStatus.value = 'error'
      lastError.value = err instanceof Error ? err.message : 'Unknown error'
      throw err
    }
  }

  async function rejectProposal(proposal: Proposal) {
    if (!storeId.value) throw new Error('No store selected')

    await themeWorkspaceService.rejectProposal(storeId.value, proposal.proposal_id)
  }

  /* ------------------------------------------------------------------ */
  /*  Actions                                                            */
  /* ------------------------------------------------------------------ */
  async function load(newStoreId: string, newPage: string, storefront: StorefrontMetadata = {}) {
    isLoading.value = true
    saveStatus.value = 'saving'
    lastError.value = null
    try {
      storeId.value = newStoreId
      page.value = newPage || 'home'
      storeSubdomain.value = storefront.subdomain?.trim() || null
      draft.value = null
      appliedOverlay.value = null
      schema.value = null
      sectionsByPage.value = {}
      iframePreviewToken.value = null
      restoreUndoStackForStore(newStoreId)

      const { data: wsData } = await themeWorkspaceService.get(newStoreId)
      const workspace = wsData.data as WorkspaceState
      draft.value = workspace.draft
      appliedOverlay.value = workspace.applied_overlay
      iframePreviewToken.value = null
      await loadHistory()

      try {
        const { token } = await mintPreviewToken()
        iframePreviewToken.value = token
      } catch {
        iframePreviewToken.value = null
      }

      // Load schema from the draft's base theme, if available
      if (workspace.draft?.base_theme_id) {
        try {
          const { data: schemaData } = await themeService.schema(workspace.draft.base_theme_id)
          schema.value = schemaData.data as ThemeSchema
          syncSectionsFromSchema(schema.value, workspace.draft?.draft_overlay)
        } catch {
          schema.value = null
          sectionsByPage.value = {}
        }
      } else {
        sectionsByPage.value = {}
      }

      saveStatus.value = 'idle'
    } catch (err: unknown) {
      saveStatus.value = 'error'
      lastError.value = err instanceof Error ? err.message : 'Unknown error'
    } finally {
      isLoading.value = false
    }
  }

  async function reload() {
    if (!storeId.value) return
    await load(storeId.value, page.value)
  }

  function reset() {
    const previousStoreId = storeId.value
    storeId.value = null
    page.value = 'home'
    draft.value = null
    appliedOverlay.value = null
    schema.value = null
    sectionsByPage.value = {}
    storeSubdomain.value = null
    iframePreviewToken.value = null
    canvasIframe.value = null
    saveStatus.value = 'idle'
    lastError.value = null
    if (previousStoreId) {
      persistUndoStackForStore(previousStoreId, [])
    }
    undoStack.value = []
    historyEntries.value = []
    historyCurrentSequence.value = null
    canUndo.value = false
    canRedo.value = false
  }

  function applyHistoryState(state: WorkspaceHistoryState) {
    if (state.workspace) {
      applyWorkspaceState(state.workspace)
    }

    historyEntries.value = Array.isArray(state.history) ? state.history : []
    historyCurrentSequence.value = state.current_sequence ?? null
    canUndo.value = Boolean(state.can_undo)
    canRedo.value = Boolean(state.can_redo)
  }

  async function loadHistory() {
    if (!storeId.value) return

    try {
      const response = await themeWorkspaceService.history(storeId.value)
      if (response?.data?.data) {
        applyHistoryState(response.data.data)
      }
    } catch {
      historyEntries.value = []
      historyCurrentSequence.value = null
      canUndo.value = undoStack.value.length > 0
      canRedo.value = false
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Mutations — all return full WorkspaceState                         */
  /* ------------------------------------------------------------------ */
  async function _mutate<T>(
    label: string,
    fn: () => Promise<T>,
    extractOverlay?: (data: T) => Overlay,
    afterApply?: (workspace: WorkspaceState) => void,
  ) {
    if (!storeId.value) return
    saveStatus.value = 'saving'
    lastError.value = null
    const priorSectionsByPage = cloneSectionsByPage(sectionsByPage.value)
    try {
      const res = (await fn()) as { data: { data: WorkspaceState } }
      const ws = res.data.data
      draft.value = ws.draft
      appliedOverlay.value = ws.applied_overlay
      afterApply?.(ws)
      broadcastDraftOverlayToIframe()

      // Push undo entry if we captured the prior overlay
      if (extractOverlay && draft.value?.draft_overlay && suppressUndoCapture === 0) {
        pushUndo({
          id: crypto.randomUUID?.() || `${Date.now()}`,
          label,
          timestamp: Date.now(),
          revertOverlay: extractOverlay(res as unknown as T),
          revertSectionsByPage: priorSectionsByPage,
        })
      }

      await loadHistory()
      saveStatus.value = 'idle'
      return ws
    } catch (err: unknown) {
      saveStatus.value = 'error'
      lastError.value = err instanceof Error ? err.message : 'Unknown error'
      throw err
    }
  }

  async function updateStyle(patch: Record<string, unknown>) {
    const prior = draft.value?.draft_overlay ? cloneJson(draft.value.draft_overlay) : {}
    const result = await _mutate(
      'update style',
      () => themeWorkspaceService.patchStyle(storeId.value!, { style: patch }),
      () => prior,
    )

    return result
  }

  async function updateBranding(patch: Record<string, unknown>) {
    const prior = draft.value?.draft_overlay ? cloneJson(draft.value.draft_overlay) : {}
    return _mutate(
      'update branding',
      () => themeWorkspaceService.patchBranding(storeId.value!, { branding: patch }),
      () => prior,
    )
  }

  async function updateSectionContent(sectionId: string, content: Record<string, unknown>) {
    const prior = draft.value?.draft_overlay ? cloneJson(draft.value.draft_overlay) : {}
    return _mutate(
      'update section content',
      () => themeWorkspaceService.patchSectionContent(storeId.value!, sectionId, { content }),
      () => prior,
    )
  }

  async function updateSectionSettings(sectionId: string, settings: Record<string, unknown>) {
    const prior = draft.value?.draft_overlay ? cloneJson(draft.value.draft_overlay) : {}
    return _mutate(
      'update section settings',
      () => themeWorkspaceService.patchSectionSettings(storeId.value!, sectionId, { settings }),
      () => prior,
    )
  }

  async function toggleSectionVisibility(sectionId: string, visible: boolean) {
    const prior = draft.value?.draft_overlay ? cloneJson(draft.value.draft_overlay) : {}
    return _mutate(
      'toggle visibility',
      () => themeWorkspaceService.patchSectionVisibility(storeId.value!, sectionId, { visible }),
      () => prior,
      () => {
        updateSectionInstance(sectionId, (section) => ({
          ...section,
          visible,
        }))
      },
    )
  }

  async function swapSectionVariant(sectionId: string, variantId: string) {
    const prior = draft.value?.draft_overlay ? cloneJson(draft.value.draft_overlay) : {}
    return _mutate(
      'swap variant',
      () => themeWorkspaceService.swapSectionVariant(storeId.value!, sectionId, { variant_id: variantId }),
      () => prior,
      () => {
        updateSectionInstance(sectionId, (section) => {
          const nextVariant = section.variants.find((variant) => variant.variant_id === variantId)

          return {
            ...section,
            variant_id: variantId,
            template_key: nextVariant?.template_key ?? section.template_key,
          }
        })
      },
    )
  }

  async function addSection(sectionTypeCode: string, pageName?: string, sortOrder?: number) {
    const prior = draft.value?.draft_overlay ? cloneJson(draft.value.draft_overlay) : {}
    const targetPage = pageName || page.value
    return _mutate(
      'add section',
      () =>
        themeWorkspaceService.addSection(storeId.value!, {
          section_type_code: sectionTypeCode,
          page: targetPage,
          sort_order: sortOrder,
        }),
      () => prior,
      (workspace) => {
        const sectionType = schema.value?.section_types.find(
          (candidate) => candidate.section_type_code === sectionTypeCode,
        )
        if (!sectionType) {
          return
        }

        const currentSections = sectionsByPage.value[targetPage] ?? []
        const sectionOrderByPage = normalizeSectionOrderPatch(
          workspace.draft?.draft_overlay?._section_order,
          targetPage,
        )
        const orderedIds = sectionOrderByPage[targetPage] ?? []
        const nextSectionId = orderedIds.find(
          (id) => !currentSections.some((section) => section.theme_section_id === id),
        ) ?? `local:${targetPage}:${sectionTypeCode}:${Date.now()}`

        const nextSection = createSectionInstance(
          sectionType,
          nextSectionId,
          targetPage,
          sortOrder ?? currentSections.length,
        )

        sectionsByPage.value = {
          ...sectionsByPage.value,
          [targetPage]: reorderPageSections(
            [...currentSections, nextSection],
            orderedIds.length > 0
              ? orderedIds
              : [...currentSections.map((section) => section.theme_section_id), nextSectionId],
          ),
        }
      },
    )
  }

  async function removeSection(sectionId: string) {
    const prior = draft.value?.draft_overlay ? cloneJson(draft.value.draft_overlay) : {}
    return _mutate(
      'remove section',
      () => themeWorkspaceService.removeSection(storeId.value!, sectionId),
      () => prior,
      () => {
        removeSectionInstance(sectionId)
      },
    )
  }

  async function reorderSections(sectionIds: string[], pageName?: string) {
    const prior = draft.value?.draft_overlay ? cloneJson(draft.value.draft_overlay) : {}
    const targetPage = pageName || page.value
    return _mutate(
      'reorder sections',
      () =>
        themeWorkspaceService.reorderSections(storeId.value!, {
          section_ids: sectionIds,
          page: targetPage,
        }),
      () => prior,
      () => {
        sectionsByPage.value = {
          ...sectionsByPage.value,
          [targetPage]: reorderPageSections(sectionsByPage.value[targetPage] ?? [], sectionIds),
        }
      },
    )
  }

  async function publish() {
    if (!storeId.value) return
    isPublishing.value = true
    lastError.value = null
    try {
      const { data } = await themeWorkspaceService.publish(storeId.value)
      const ws = data.data as WorkspaceState
      draft.value = ws.draft
      appliedOverlay.value = ws.applied_overlay
      saveStatus.value = 'idle'
      return ws
    } catch (err: unknown) {
      saveStatus.value = 'error'
      lastError.value = err instanceof Error ? err.message : 'Unknown error'
      throw err
    } finally {
      isPublishing.value = false
    }
  }

  async function mintPreviewToken(): Promise<{ url: string; token: string }> {
    if (!storeId.value) throw new Error('No store selected')
    const { data } = await themeWorkspaceService.previewToken(storeId.value)
    const result = data.data as { preview_url: string; token: string }
    return { url: result.preview_url, token: result.token }
  }

  /* ------------------------------------------------------------------ */
  /*  Undo                                                               */
  /* ------------------------------------------------------------------ */
  function pushUndo(entry: UndoEntry) {
    undoStack.value.push(entry)
    if (undoStack.value.length > 10) {
      undoStack.value.shift()
    }
    persistUndoStack()
  }

  function restoreUndoStackForStore(targetStoreId: string) {
    undoStack.value = cloneJson(undoStackByStore.value[targetStoreId] ?? [])
  }

  function persistUndoStackForStore(targetStoreId: string, entries: UndoEntry[]) {
    const next = { ...undoStackByStore.value }
    if (entries.length) {
      next[targetStoreId] = cloneJson(entries).slice(-10)
    } else {
      delete next[targetStoreId]
    }
    undoStackByStore.value = next
    writeStoredUndoStackByStore(next)
  }

  function persistUndoStack() {
    if (!storeId.value) return
    persistUndoStackForStore(storeId.value, undoStack.value)
  }

  async function restoreUndoSnapshot(entry: UndoEntry) {
    const targetOverlay = entry.revertOverlay ?? {}
    const targetSectionsByPage = cloneSectionsByPage(entry.revertSectionsByPage)
    const targetPages = new Set([
      ...Object.keys(sectionsByPage.value),
      ...Object.keys(targetSectionsByPage),
    ])
    const restoredSectionIds = new Map<string, string>()

    const stylePatch = buildSparseRestorePatch(
      (draft.value?.draft_overlay?._style as Record<string, unknown> | undefined) ?? {},
      (targetOverlay._style as Record<string, unknown> | undefined) ?? {},
    )
    if (Object.keys(stylePatch).length > 0) {
      await updateStyle(stylePatch)
    }

    const brandingPatch = buildSparseRestorePatch(
      (draft.value?.draft_overlay?._branding as Record<string, unknown> | undefined) ?? {},
      (targetOverlay._branding as Record<string, unknown> | undefined) ?? {},
    )
    if (Object.keys(brandingPatch).length > 0) {
      await updateBranding(brandingPatch)
    }

    for (const pageKey of targetPages) {
      const targetSections = targetSectionsByPage[pageKey] ?? []
      const targetSectionIdSet = new Set(targetSections.map((section) => section.theme_section_id))

      for (const currentSection of [...(sectionsByPage.value[pageKey] ?? [])]) {
        if (!targetSectionIdSet.has(currentSection.theme_section_id)) {
          await removeSection(currentSection.theme_section_id)
        }
      }

      for (const targetSection of targetSections) {
        const currentSectionIds = new Set(
          (sectionsByPage.value[pageKey] ?? []).map((section) => section.theme_section_id),
        )

        if (currentSectionIds.has(targetSection.theme_section_id)) {
          restoredSectionIds.set(targetSection.theme_section_id, targetSection.theme_section_id)
          continue
        }

        const beforeIds = new Set(
          (sectionsByPage.value[pageKey] ?? []).map((section) => section.theme_section_id),
        )

        // There is no full revert endpoint today, so undo recreates missing sections via add/remove APIs.
        await addSection(targetSection.section_type_code, pageKey, targetSection.sort_order)

        const createdSection = (sectionsByPage.value[pageKey] ?? []).find(
          (section) => !beforeIds.has(section.theme_section_id),
        )

        if (createdSection) {
          restoredSectionIds.set(targetSection.theme_section_id, createdSection.theme_section_id)
        }
      }

      const targetOrder = targetSections
        .map((section) => restoredSectionIds.get(section.theme_section_id) ?? section.theme_section_id)
        .filter((sectionId) =>
          (sectionsByPage.value[pageKey] ?? []).some((section) => section.theme_section_id === sectionId),
        )

      if (targetOrder.length > 0) {
        const currentOrder = (sectionsByPage.value[pageKey] ?? []).map((section) => section.theme_section_id)
        if (!deepEqual(currentOrder, targetOrder)) {
          await reorderSections(targetOrder, pageKey)
        }
      }

      for (const targetSection of targetSections) {
        const liveSectionId = restoredSectionIds.get(targetSection.theme_section_id) ?? targetSection.theme_section_id
        const liveSection = findSectionById(liveSectionId)?.section
        if (!liveSection) {
          continue
        }

        if (liveSection.variant_id !== targetSection.variant_id) {
          await swapSectionVariant(liveSectionId, targetSection.variant_id)
        }

        if (liveSection.visible !== targetSection.visible) {
          await toggleSectionVisibility(liveSectionId, targetSection.visible)
        }

        const targetSectionOverlay =
          (targetOverlay[targetSection.section_type_code] as ProposalSectionPatch | undefined) ?? {}
        const currentSectionOverlay =
          (draft.value?.draft_overlay?.[liveSection.section_type_code] as ProposalSectionPatch | undefined)
          ?? {}

        const contentPatch = buildSparseRestorePatch(
          currentSectionOverlay.content,
          targetSectionOverlay.content,
        )
        if (Object.keys(contentPatch).length > 0) {
          await updateSectionContent(liveSectionId, contentPatch)
        }

        const settingsPatch = buildSparseRestorePatch(
          currentSectionOverlay.settings,
          targetSectionOverlay.settings,
        )
        if (Object.keys(settingsPatch).length > 0) {
          await updateSectionSettings(liveSectionId, settingsPatch)
        }
      }
    }
  }

  async function undoLast() {
    if (canUndo.value) {
      await undoFromServer()
      return
    }

    const entry = undoStack.value.pop()
    if (!entry || !storeId.value) return
    persistUndoStack()
    saveStatus.value = 'saving'
    suppressUndoCapture += 1
    try {
      await restoreUndoSnapshot(entry)
      saveStatus.value = 'idle'
    } catch (err: unknown) {
      undoStack.value.push(entry)
      persistUndoStack()
      saveStatus.value = 'error'
      lastError.value = err instanceof Error ? err.message : 'Unknown error'
    } finally {
      suppressUndoCapture = Math.max(0, suppressUndoCapture - 1)
    }
  }

  async function undoFromServer() {
    if (!storeId.value) return
    saveStatus.value = 'saving'
    lastError.value = null
    try {
      const { data } = await themeWorkspaceService.undo(storeId.value)
      applyHistoryState(data.data)
      saveStatus.value = 'idle'
    } catch (err: unknown) {
      saveStatus.value = 'error'
      lastError.value = err instanceof Error ? err.message : 'Unknown error'
      throw err
    }
  }

  async function redoFromServer() {
    if (!storeId.value) return
    saveStatus.value = 'saving'
    lastError.value = null
    try {
      const { data } = await themeWorkspaceService.redo(storeId.value)
      applyHistoryState(data.data)
      saveStatus.value = 'idle'
    } catch (err: unknown) {
      saveStatus.value = 'error'
      lastError.value = err instanceof Error ? err.message : 'Unknown error'
      throw err
    }
  }

  async function revertToSnapshot(snapshotId: string) {
    if (!storeId.value) return
    saveStatus.value = 'saving'
    lastError.value = null
    try {
      const { data } = await themeWorkspaceService.revertTo(storeId.value, snapshotId)
      applyHistoryState(data.data)
      saveStatus.value = 'idle'
    } catch (err: unknown) {
      saveStatus.value = 'error'
      lastError.value = err instanceof Error ? err.message : 'Unknown error'
      throw err
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Iframe bridge                                                      */
  /* ------------------------------------------------------------------ */
  let previewRevertTimer: ReturnType<typeof setTimeout> | null = null

  function resolveCanvasTargetOrigin() {
    const iframeSrc = canvasIframe.value?.src?.trim()
    if (!iframeSrc) {
      return null
    }

    try {
      return new URL(iframeSrc).origin
    } catch {
      return null
    }
  }

  function postOverlayToIframe(iframeWindow: Window | null | undefined, overlay: Overlay) {
    const targetOrigin = resolveCanvasTargetOrigin()
    if (!iframeWindow || !targetOrigin) {
      return
    }

    iframeWindow.postMessage({ type: 'overlay:patch', patch: cloneJson(overlay) }, targetOrigin)
  }

  function broadcastDraftOverlayToIframe() {
    const overlay = draft.value?.draft_overlay
    if (!overlay) {
      return
    }

    postOverlayToIframe(canvasIframe.value?.contentWindow, overlay)
  }

  function postPreviewToIframe(
    iframeWindow: Window | null | undefined,
    previewOverlay: Overlay,
    currentOverlay: Overlay,
    durationMs = 10000,
    focusSectionCode?: string | null,
  ) {
    const targetOrigin = resolveCanvasTargetOrigin()
    if (!iframeWindow || !targetOrigin) {
      return
    }

    iframeWindow.postMessage({
      type: 'overlay:patch',
      patch: cloneJson(previewOverlay),
      focus_section_code: focusSectionCode ?? null,
    }, targetOrigin)
    if (previewRevertTimer) clearTimeout(previewRevertTimer)
    previewRevertTimer = null
    if (durationMs > 0) {
      previewRevertTimer = setTimeout(() => {
        iframeWindow.postMessage({ type: 'overlay:patch', patch: cloneJson(currentOverlay) }, targetOrigin)
      }, durationMs)
    }
  }

  function registerCanvasIframe(iframeElement: HTMLIFrameElement | null) {
    canvasIframe.value = iframeElement
  }

  /* ------------------------------------------------------------------ */
  /*  Return                                                             */
  /* ------------------------------------------------------------------ */
  return {
    // state
    storeId,
    page,
    draft,
    appliedOverlay,
    schema,
    sectionsByPage,
    storeSubdomain,
    iframePreviewToken,
    canvasIframe,
    // UI
    saveStatus,
    lastError,
    isLoading,
    isPublishing,
    viewport,
    historyEntries,
    historyCurrentSequence,
    canUndo,
    canRedo,
    // derived
    hasUnsavedChanges,
    isHybridPage,
    currentPageSections,
    applyWorkspaceState,
    // actions
    load,
    reload,
    reset,
    updateStyle,
    updateBranding,
    updateSectionContent,
    updateSectionSettings,
    toggleSectionVisibility,
    swapSectionVariant,
    addSection,
    removeSection,
    reorderSections,
    confirmProposal,
    rejectProposal,
    publish,
    mintPreviewToken,
    registerCanvasIframe,
    // undo
    undoStack,
    pushUndo,
    undoLast,
    loadHistory,
    undoFromServer,
    redoFromServer,
    revertToSnapshot,
    // iframe
    postOverlayToIframe,
    postPreviewToIframe,
  }
})
